import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, TrendingUp } from "lucide-react";
import { MonthPicker } from "../components/MonthPicker";
import { useCategories } from "../hooks/useCategories";
import { categoryColor } from "../charts/colors";
import { categoryLabels, formatSEK, formatMonth } from "../lib/format";
import type { Budget, SpendingSummary, MainCategory } from "../types";

interface BudgetPageProps {
  budget: Budget;
  summary: SpendingSummary;
  allMonths: string[];
  selectedMonth: string | null;
  onMonthChange: (month: string | null) => void;
  onSaveBudget: (budget: Budget) => Promise<void>;
}

/** Parse a user-typed amount: "12 500,50" -> 12500.5, invalid -> 0 */
function parseAmount(raw: string): number {
  const value = Number.parseFloat(
    raw.replace(/\s/g, "").replace(",", ".")
  );
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1]! + sorted[mid]!) / 2
    : sorted[mid]!;
}

function budgetToInputs(budget: Budget): Record<string, string> {
  const values: Record<string, string> = {};
  for (const [category, amount] of Object.entries(budget.categories)) {
    values[category] = String(amount);
  }
  return values;
}

export function BudgetPage({
  budget,
  summary,
  allMonths,
  selectedMonth,
  onMonthChange,
  onSaveBudget,
}: BudgetPageProps) {
  const { allCategories } = useCategories();

  const [values, setValues] = useState<Record<string, string>>(() =>
    budgetToInputs(budget)
  );
  const [incomeTargetInput, setIncomeTargetInput] = useState(() =>
    budget.incomeTarget ? String(budget.incomeTarget) : ""
  );
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const baseline = useRef(JSON.stringify(budget));

  // Resync inputs only when the saved budget actually changes (e.g. after a
  // save), so unrelated refetches don't clobber unsaved edits.
  useEffect(() => {
    const next = JSON.stringify(budget);
    if (next !== baseline.current) {
      baseline.current = next;
      setValues(budgetToInputs(budget));
      setIncomeTargetInput(
        budget.incomeTarget ? String(budget.incomeTarget) : ""
      );
    }
  }, [budget]);

  // Budgetable categories: everything except income & transfers, including
  // custom categories found in the data and categories that only exist in the
  // saved budget (so their amounts stay visible and editable).
  const baseCategories = allCategories.filter(
    (c) => c.value !== "income" && c.value !== "transfer"
  );
  const knownCategoryKeys = new Set(baseCategories.map((c) => c.value));
  const budgetCategories = [
    ...baseCategories,
    ...Object.keys(values)
      .filter(
        (key) =>
          !knownCategoryKeys.has(key) &&
          key !== "income" &&
          key !== "transfer"
      )
      .map((key) => ({ value: key, label: categoryLabels[key] ?? key })),
  ];

  // --- Income per month (byMonth always covers all months, transfers excluded) ---
  const months = summary.byMonth;
  const incomes = months.map((m) => m.income);
  const avgIncome =
    incomes.length > 0
      ? incomes.reduce((sum, v) => sum + v, 0) / incomes.length
      : 0;
  const medianIncome = median(incomes);

  // --- Editor totals ---
  const totalBudgetInput = Object.values(values).reduce(
    (sum, raw) => sum + parseAmount(raw),
    0
  );
  const incomeTarget = parseAmount(incomeTargetInput);
  const marginAgainstTarget = incomeTarget > 0 ? incomeTarget - totalBudgetInput : null;

  function setCategoryValue(category: string, raw: string) {
    setJustSaved(false);
    setValues((prev) => ({ ...prev, [category]: raw }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const categories: Record<string, number> = {};
      for (const [category, raw] of Object.entries(values)) {
        const amount = parseAmount(raw);
        if (amount > 0) categories[category] = amount;
      }
      await onSaveBudget({
        incomeTarget: incomeTarget > 0 ? incomeTarget : null,
        categories,
      });
      setJustSaved(true);
    } finally {
      setSaving(false);
    }
  }

  // --- Actual vs budget (saved budget, not unsaved edits) ---
  const monthCount = Math.max(months.length, 1);
  const savedCategories = budget.categories;
  const totalSavedBudget = Object.values(savedCategories).reduce(
    (sum, v) => sum + v,
    0
  );

  // summary.byCategory is scoped to the selected month; with "Alla" it covers
  // everything, so divide by the number of months to get a monthly average.
  const actualByCategory: Record<string, number> = {};
  for (const [category, value] of Object.entries(summary.byCategory)) {
    if (category === "transfer") continue;
    actualByCategory[category] = selectedMonth
      ? (value as number)
      : (value as number) / monthCount;
  }

  const comparisonRows = [
    ...new Set([
      ...Object.keys(savedCategories),
      ...Object.keys(actualByCategory),
    ]),
  ]
    .map((category) => ({
      category,
      label: categoryLabels[category] ?? category,
      color: categoryColor(category),
      actual: actualByCategory[category] ?? 0,
      budget: savedCategories[category] ?? 0,
    }))
    .sort((a, b) => b.actual - a.actual);

  const totalActual = comparisonRows.reduce((sum, r) => sum + r.actual, 0);
  const comparisonLabel = selectedMonth
    ? formatMonth(selectedMonth)
    : `snitt per månad (${monthCount} mån)`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-bold">Budget</h2>
        <MonthPicker
          value={selectedMonth}
          availableMonths={allMonths}
          onChange={onMonthChange}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {/* --- Budget editor --- */}
        <Card>
          <CardHeader>
            <CardTitle>Månadsbudget</CardTitle>
            <p className="text-xs text-muted-foreground">
              Sätt ett belopp per kategori. Lämna tomt för kategorier utan
              budget.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <label
                  htmlFor="income-target"
                  className="text-sm font-medium flex items-center gap-1.5"
                >
                  <TrendingUp className="h-4 w-4 text-success" />
                  Inkomstmål per månad
                </label>
                <Input
                  id="income-target"
                  className="h-8 w-[140px] text-right tabular-nums"
                  inputMode="decimal"
                  placeholder="0"
                  value={incomeTargetInput}
                  onChange={(e) => {
                    setJustSaved(false);
                    setIncomeTargetInput(e.target.value);
                  }}
                />
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={avgIncome <= 0}
                  onClick={() => {
                    setJustSaved(false);
                    setIncomeTargetInput(String(Math.round(avgIncome)));
                  }}
                >
                  Använd snitt ({formatSEK(Math.round(avgIncome))})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={medianIncome <= 0}
                  onClick={() => {
                    setJustSaved(false);
                    setIncomeTargetInput(String(Math.round(medianIncome)));
                  }}
                >
                  Använd median ({formatSEK(Math.round(medianIncome))})
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              {budgetCategories.map((category) => (
                <div
                  key={category.value}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="flex items-center gap-2 text-sm min-w-0">
                    <span
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: categoryColor(category.value) }}
                    />
                    <span className="truncate">{category.label}</span>
                  </span>
                  <Input
                    className="h-8 w-[140px] text-right tabular-nums"
                    inputMode="decimal"
                    placeholder="0"
                    value={values[category.value] ?? ""}
                    onChange={(e) =>
                      setCategoryValue(category.value, e.target.value)
                    }
                  />
                </div>
              ))}
            </div>

            <div className="border-t pt-3 space-y-1.5">
              <div className="flex items-center justify-between text-sm font-semibold">
                <span>Totalt budget</span>
                <span className="tabular-nums">
                  {formatSEK(totalBudgetInput)}
                </span>
              </div>
              {marginAgainstTarget !== null && (
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Kvar av inkomstmålet</span>
                  <span
                    className={`tabular-nums font-medium ${
                      marginAgainstTarget >= 0
                        ? "text-success"
                        : "text-destructive"
                    }`}
                  >
                    {marginAgainstTarget >= 0 ? "+" : ""}
                    {formatSEK(marginAgainstTarget)}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 pt-2">
                <Button onClick={() => void handleSave()} disabled={saving}>
                  {saving ? "Sparar..." : "Spara budget"}
                </Button>
                {justSaved && (
                  <span className="flex items-center gap-1 text-sm text-success">
                    <Check className="h-4 w-4" /> Sparad
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* --- Actual vs budget --- */}
        <Card>
          <CardHeader>
            <CardTitle>Utfall mot budget</CardTitle>
            <p className="text-xs text-muted-foreground">
              Faktiska utgifter ({comparisonLabel}), exklusive överföringar
            </p>
          </CardHeader>
          <CardContent>
            {totalSavedBudget <= 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Ingen budget sparad ännu. Fyll i månadsbudgeten och spara för
                att se utfallet här.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5">
                  <div className="flex items-center justify-between text-sm font-semibold">
                    <span>Totalt</span>
                    <span className="tabular-nums">
                      {formatSEK(totalActual)}{" "}
                      <span className="text-muted-foreground font-normal">
                        av {formatSEK(totalSavedBudget)}
                      </span>
                    </span>
                  </div>
                  <div className="relative h-3 overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full transition-all ${
                        totalActual > totalSavedBudget
                          ? "bg-destructive/80"
                          : "bg-success/80"
                      }`}
                      style={{
                        width: `${Math.min(
                          (totalActual / totalSavedBudget) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                  <p
                    className={`text-xs font-medium text-right ${
                      totalActual <= totalSavedBudget
                        ? "text-success"
                        : "text-destructive"
                    }`}
                  >
                    {totalActual <= totalSavedBudget
                      ? `${formatSEK(totalSavedBudget - totalActual)} kvar`
                      : `${formatSEK(totalActual - totalSavedBudget)} över budget`}
                  </p>
                </div>

                <div className="space-y-3">
                  {comparisonRows
                    .filter((row) => row.budget > 0 || row.actual > 0)
                    .map((row) => {
                      const hasBudget = row.budget > 0;
                      const over = hasBudget && row.actual > row.budget;
                      const pct = hasBudget
                        ? Math.min((row.actual / row.budget) * 100, 100)
                        : 0;
                      return (
                        <div key={row.category} className="space-y-1">
                          <div className="flex items-center justify-between gap-2 text-sm">
                            <span className="flex items-center gap-2 min-w-0">
                              <span
                                className="h-2.5 w-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: row.color }}
                              />
                              <span className="truncate">{row.label}</span>
                            </span>
                            <span className="tabular-nums text-xs text-muted-foreground whitespace-nowrap">
                              {formatSEK(row.actual)}
                              {hasBudget && <> / {formatSEK(row.budget)}</>}
                            </span>
                          </div>
                          {hasBudget ? (
                            <div className="flex items-center gap-2">
                              <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted">
                                <div
                                  className={`h-full rounded-full ${
                                    over
                                      ? "bg-destructive/80"
                                      : "bg-success/80"
                                  }`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span
                                className={`text-[11px] tabular-nums whitespace-nowrap font-medium ${
                                  over ? "text-destructive" : "text-success"
                                }`}
                              >
                                {over
                                  ? `+${formatSEK(row.actual - row.budget)}`
                                  : `${formatSEK(row.budget - row.actual)} kvar`}
                              </span>
                            </div>
                          ) : (
                            <p className="text-[11px] text-muted-foreground">
                              Ingen budget satt
                            </p>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* --- Actual income per month --- */}
      <Card>
        <CardHeader>
          <CardTitle>Faktisk inkomst per månad</CardTitle>
          <p className="text-xs text-muted-foreground">
            Exklusive överföringar — använd som underlag för inkomstmålet och
            budgeten
          </p>
        </CardHeader>
        <CardContent>
          {months.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Ingen data ännu. Ladda upp kontoutdrag för att se inkomster.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3">
                <div className="rounded-lg border px-3 py-2">
                  <p className="text-xs text-muted-foreground">
                    Genomsnitt per månad
                  </p>
                  <p className="text-lg font-bold text-success tabular-nums">
                    {formatSEK(Math.round(avgIncome))}
                  </p>
                </div>
                <div className="rounded-lg border px-3 py-2">
                  <p className="text-xs text-muted-foreground">
                    Median per månad
                  </p>
                  <p className="text-lg font-bold text-success tabular-nums">
                    {formatSEK(Math.round(medianIncome))}
                  </p>
                </div>
              </div>

              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card z-10">
                    <tr className="border-b text-left">
                      <th className="pb-2 font-medium text-muted-foreground">
                        Månad
                      </th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">
                        Inkomster
                      </th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">
                        Utgifter
                      </th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">
                        Netto
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...months].reverse().map((m) => {
                      const net = m.income - m.spending;
                      return (
                        <tr key={m.month} className="border-b border-border/50">
                          <td className="py-2 pr-3 whitespace-nowrap">
                            {formatMonth(m.month)}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums font-medium text-success">
                            {formatSEK(m.income)}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-destructive">
                            {formatSEK(m.spending)}
                          </td>
                          <td
                            className={`py-2 text-right tabular-nums font-medium ${
                              net >= 0 ? "text-success" : "text-destructive"
                            }`}
                          >
                            {net >= 0 ? "+" : ""}
                            {formatSEK(net)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
